import { useState, useCallback, useEffect, useMemo, useRef } from "react";

import {
  remindersFromActivity,
  remindersToPayload,
} from "../../plan-management/components/reminder-mapping";
import iconsCollection from "../../../../assets/icons.json";
import debounce from "../../../../common/utils/debounce";
import ApiTaskManagement from "../../../../api/task-managements";
import {
  COUNTER,
  counterConfigurationError,
  goalToPayload,
  unitsToPayload,
} from "./counter-fields";
import { colors } from "../../../../components/icons/icons";

const icons = iconsCollection.icons;

const getFieldByType = (key, value) => {
  if (key === "type" || key === "required") {
    return { [key]: value };
  }

  if (key === "answers") {
    return { answers: value.target.value.split(/\n/) };
  }

  return {
    [key]: value.target.value,
  };
};

const pickScenarioVersion = (task) => {
  const versions = task?.DailyTaskVersions || [];
  if (!versions.length) return null;

  const priority = ['pending', 'activeCopy', 'active'];
  for (const st of priority) {
    const found = versions.find(v => v.status === st);
    if (found) return found;
  }
  return versions[0];
};

export const useTaskEdit = ({
  task,
  createTask,
  updateTask,
  pages,
  cancelEditing,
  close,
}) => {
  const [title, setTitle] = useState(task?.title);
  const [description, setDescription] = useState(task?.description);
  const [categoryId, setCategoryId] = useState(task?.Category?.id);
  const [selfAssigned, setSelfAssigned] = useState(task?.selfAssigned ?? false);
  const [taskType, setTaskType] = useState(task?.taskType ?? 'regular');
  // LF360-1874. Kept as the string the input holds rather than as a number:
  // the goal box is empty for "no goal", and an empty string and a 0 are
  // different answers that `Number("")` would make the same.
  const [counterUnits, setCounterUnits] = useState(task?.counterUnits ?? '');
  const [counterGoal, setCounterGoal] = useState(
    task?.counterGoal === null || task?.counterGoal === undefined
      ? ''
      : String(task.counterGoal)
  );
  const [icon, setIcon] = useState({
    avatar: task?.avatar || icons[68].content,
    avatarColor: task?.avatarColor || "#1E6379",
    backgroundColor: task?.backgroundColor || "#B0E5F5",
  });

  const [publishedOnce, setPublishedOnce] = useState(!!task?.publishedOnce);
  const [statusSummary, setStatusSummary] = useState(task?.statusSummary);

  const [isPending, setIsPending] = useState(task?.status === 'pending');
  const [taskPages, setTaskPages] = useState(task?.pages || []);
  const [taskCohorts, setTaskCohorts] = useState(task?.cohorts || []);
  const [questions, setQuestions] = useState(
    task?.questions?.length ? task?.questions : [],
  );
  // LF360-1940: the reminders every plan gets from this task. Held as the list
  // the picker edits and stored the way an activity stores them, which is what
  // lets the backend copy rather than translate.
  const [reminders, setReminders] = useState(() =>
    remindersFromActivity(task?.defaultReminders || {})
  );
  const [canSave, setCanSave] = useState(false);
  // The task whose fresh read `canSave` was turned on for. A render with a new
  // task id comes before the effect turns `canSave` off, so `ready` asks this.
  const readTaskIdRef = useRef(null);

  const hasUserChangedRef = useRef(false);

  const resetState = (sourceTask) => {
    setTitle(sourceTask?.title || "");
    setDescription(sourceTask?.description || "");
    setCategoryId(sourceTask?.Category?.id);
    setSelfAssigned(sourceTask?.selfAssigned ?? false);
    setTaskType(sourceTask?.taskType ?? "regular");
    setCounterUnits(sourceTask?.counterUnits ?? "");
    setCounterGoal(
      sourceTask?.counterGoal === null || sourceTask?.counterGoal === undefined
        ? ""
        : String(sourceTask.counterGoal)
    );
    setIcon({
      avatar: sourceTask?.avatar || icons[68].content,
      avatarColor: sourceTask?.avatarColor || "#1E6379",
      backgroundColor: sourceTask?.backgroundColor || "#B0E5F5",
      middleColor: sourceTask?.middleColor || undefined
    });
    setTaskPages(sourceTask?.pages || []);
    setTaskCohorts(sourceTask?.cohorts || []);
    setQuestions(sourceTask?.questions || []);
    setIsPending(sourceTask?.status === "pending");
    setPublishedOnce(!!sourceTask?.publishedOnce);
    setStatusSummary(sourceTask?.statusSummary);
    setReminders(remindersFromActivity(sourceTask?.defaultReminders || {}));

    hasUserChangedRef.current = false;
  };

  useEffect(() => {
    setCanSave(false); // Disable saving while loading/resetting

    if (task?.id) {
      // Existing task: fetch latest data
      ApiTaskManagement.getById(task.id)
        .then(response => {
          const freshTask = response?.data?.task || response?.data;
          resetState(freshTask);
        })
        .catch(error => {
          console.error("Failed to fetch latest task data, falling back to prop data.", error);
          resetState(task);
        })
        .finally(() => {
          setTimeout(() => {
            readTaskIdRef.current = task.id;
            setCanSave(true);
          }, 500);
        });
    } else {
      // New task: reset with empty/default values and enable saving
      resetState(null);
      setCanSave(true);
    }
  }, [task?.id]);
 
  useEffect(() => {
    setIsPending(task?.status === "pending");
  }, [task?.status]);

  const onReset = () => {
    if (task?.id) {
      setCanSave(false); // Disable saving during reset
      ApiTaskManagement.getById(task.id)
        .then(response => {
          const freshTask = response?.data?.task || response?.data;
          resetState(freshTask);
          setIsPending(false); // Explicitly set pending to false on cancel
        })
        .finally(() => {
          setTimeout(() => setCanSave(true), 500);
        });
    }
  };

  const debouncedAutoSave = useCallback(
    debounce((fields) => {
      if (task?.id && typeof updateTask === "function") {
        updateTask(task.id, fields, { silent: true });
      }
    }, 500),
    [task?.id, updateTask]
  );

  /**
   * LF360-1874 — the two counter fields, as the API takes them.
   *
   * Sent on every save rather than only when the type is counter, because the
   * server reads an absent field as "unchanged" and a task that has just
   * stopped being a counter has to say so. Nulled here for the same reason it
   * is nulled there: a type change leaves the configuration behind otherwise,
   * and it comes back the moment somebody switches the type again.
   */
  const counterPayload = useMemo(() => (
    taskType === COUNTER
      ? {
        counterUnits: unitsToPayload(counterUnits),
        counterGoal: goalToPayload(counterGoal),
      }
      : { counterUnits: null, counterGoal: null }
  ), [taskType, counterUnits, counterGoal]);

  const counterError = useMemo(
    () => counterConfigurationError({ taskType, counterUnits, counterGoal }),
    [taskType, counterUnits, counterGoal]
  );

  const triggerAutoSave = () => {
    debouncedAutoSave({
      title,
      description,
      selfAssigned,
      taskType,
      ...counterPayload,
      questions: (questions || []).map((q) => ({
        ...q,
        text: q.title || q.text,
        answers:
          q.type === "openText"
            ? [""]
            : (q.answers || [])
                .map((a) => (typeof a === "string" ? a : a.text))
                .filter((item) => item && item !== ""),
      })),
      pages: taskPages,
      cohorts: taskCohorts,
      categoryId,
      avatar: icon.avatar,
      avatarColor: icon.avatarColor,
      backgroundColor: icon.backgroundColor,
      middleColor: icon.middleColor,
      defaultReminders: remindersToPayload(reminders),
    });
  };

  useEffect(() => {
    if (!canSave || !task?.id || !isPending || !hasUserChangedRef.current) {
      return;
    }

    // LF360-1874: a counter the server would refuse is not autosaved. Without
    // this the draft reports itself saved while every autosave comes back 400,
    // and the admin finds out when they publish.
    if (counterError) {
      return;
    }

    triggerAutoSave();
  }, [
    canSave,
    isPending,
    counterError,
    title,
    description,
    selfAssigned,
    taskType,
    counterUnits,
    counterGoal,
    questions,
    taskPages,
    taskCohorts,
    categoryId,
    icon,
    reminders,
  ]);

  const markChanged = () => {
    setIsPending(true);
    hasUserChangedRef.current = true;
  };

  const updateFormFields = (questionKey, key) => (value) => {
    let updatedQuestions = questions ? [...questions] : [];

    updatedQuestions[questionKey] = {
      ...updatedQuestions[questionKey],
      ...getFieldByType(key, value),
    };
    setQuestions(updatedQuestions);
  };

  const remove = (idx) => {
    const list = questions
      .filter((q) => q.key !== idx)
      .map((q, index) => ({ ...q, key: index }));

    setQuestions(list);
  };

  const addQuestion = () => {
    const newList = [
      ...(questions || []),
      {
        key: questions?.length ? questions.length : 0,
        title: `Question`,
        type: "openText",
      },
    ];

    setQuestions(newList);
  };

  const create = () => {
    if (!task.id) {
      createTask({
        id: task.id,
        title,
        description,
        categoryId,
        selfAssigned,
        questions: (questions || []).map((q) => ({
          ...q,
          answers: q.type === "openText" ? [""] : q.answers,
        })),
        pages: taskPages,
        cohorts: taskCohorts,
        taskType,
        ...counterPayload,
        defaultReminders: remindersToPayload(reminders),
        ...icon,
      });
    } else {
      updateTask(task.id, {
        title,
        description,
        selfAssigned,
        taskType,
        ...counterPayload,
        questions: (questions || []).map((q) => ({
          ...q,
          text: q.title || q.text,
          answers:
            q.type === "openText"
              ? [""]
              : (q.answers || [])
                  .map((a) => {
                    if (typeof a === "string") return a;
                    return a.text;
                  })
                  .filter((item) => item && item !== ""),
        })),
        pages: taskPages,
        cohorts: taskCohorts,
        categoryId,
        avatar: icon.avatar,
        avatarColor: icon.avatarColor,
        backgroundColor: icon.backgroundColor,
        middleColor: icon.middleColor,
        defaultReminders: remindersToPayload(reminders),
      });
    }
  };

  const pickMiddleByBg = (bg) =>
    (colors.find(c => c.backgroundColor?.toLowerCase() === (bg || "").toLowerCase())?.middleColor) || undefined;

  const onIconSelect = (selected) => {
    const nextBg = selected.backgroundColor || icon.backgroundColor;

    const newIcon = {
      avatar: selected.avatar || icon.avatar,
      avatarColor: selected.avatarColor || icon.avatarColor,
      backgroundColor: nextBg,
      middleColor: pickMiddleByBg(nextBg)
    };
    setIcon(newIcon);
  };

  // const onAddPage = (id) => {
  //   setTaskPages((prev) => {
  //     return [...prev, pages.find((page) => page.id === id)];
  //   });
  // };

  const onRemovePage = (id) => {
    setTaskPages((prev) => {
      return prev.filter((page) => page.id !== id);
    });
  };

  const updateCohorts = (values) => {
    setTaskCohorts(values);
  };

  const cancel = () => {
    if (task?.id) {
      onReset();
      cancelEditing();
      return;
    }
    close();
  };

  // LF360-2030: the content search hands over the articles it found, which the
  // list loaded with the task table need not contain — it used to be looked up
  // there, and an article it did not have was dropped without a word. An id is
  // still looked up, for any caller that has only that.
  const onAddPages = (entries = []) => {
    const idSet = new Set((taskPages || []).map(p => p.id));
    const toAdd = (entries || [])
      .filter(Boolean)
      .map(entry => (typeof entry === 'object' ? entry : (pages || []).find(p => p.id === entry)))
      .filter(page => page && !idSet.has(page.id) && idSet.add(page.id));

    if (!toAdd.length) return;

    setTaskPages(prev => [...(prev || []), ...toAdd]);
  };

  return {
    save: create,
    cancel,
    // The fresh read of this task has landed and changes are saved from now on.
    ready: canSave && !!task?.id && readTaskIdRef.current === task.id,
    id: task?.id,
    statusSummary,
    publishedOnce,
    title: {
      value: title,
      update: (e) => {
        markChanged();
        setTitle(e.target.value);
      },
    },
    description: {
      value: description,
      // The rich editor hands over HTML, the old textarea handed over an event
      // (LF360-1852) — both are accepted so nothing else has to change.
      update: (value) => {
        markChanged();
        setDescription(
          typeof value === "string" ? value : (value?.target?.value ?? "")
        );
      },
    },
    questions: {
      value: questions,
      update: (questionKey, key) => (value) => {
        markChanged();
        updateFormFields(questionKey, key)(value);
      },
      add: () => {
        markChanged();
        addQuestion();
      },
      remove: (idx) => {
        markChanged();
        remove(idx);
      },
    },
    // pages: {
    //   value: taskPages,
    //   add: (id) => {
    //     markChanged();
    //     onAddPage(id);
    //   },
    //   remove: (id) => {
    //     markChanged();
    //     onRemovePage(id);
    //   },
    // },
    pages: {
      value: taskPages,
      add: (pagesOrIds) => {
        markChanged();
        onAddPages(Array.isArray(pagesOrIds) ? pagesOrIds : [pagesOrIds]);
      },
      remove: (id) => {
        markChanged();
        onRemovePage(id);
      },
    },
    icon: {
      value: icon,
      update: (selected) => {
        markChanged();
        onIconSelect(selected);
      },
    },
    category: {
      value: categoryId,
      change: (value) => {
        markChanged();
        setCategoryId(value);
      },
    },
    selfAssigned: {
      value: selfAssigned,
      change: (value) => {
        markChanged();
        setSelfAssigned(value);
      },
    },
    taskType: {
      value: taskType,
      change: (value) => {
        markChanged();
        setTaskType(value);

        // LF360-1874: the form forgets them as the server does, so switching
        // away and back does not bring the old units silently with it.
        if (value !== COUNTER) {
          setCounterUnits('');
          setCounterGoal('');
        }
      },
    },
    counter: {
      units: counterUnits,
      goal: counterGoal,
      error: counterError,
      changeUnits: (value) => {
        markChanged();
        setCounterUnits(value);
      },
      changeGoal: (value) => {
        markChanged();
        setCounterGoal(value);
      },
    },
    cohorts: {
      value: taskCohorts,
      onChange: (values) => {
        markChanged();
        updateCohorts(values);
      },
      onCreate: (values) => {
        markChanged();
        updateCohorts(values);
      },
    },
    reminders: {
      value: reminders,
      change: (next) => {
        markChanged();
        setReminders(next);
      },
    },
    DailyTaskVersion: pickScenarioVersion(task),
    // DailyTaskVersion:
    //   task?.DailyTaskVersions && task.DailyTaskVersions[0]
    //     ? task.DailyTaskVersions[0]
    //     : null,
  };
};